/**
 * Générateur — Soustraire deux nombres décimaux relatifs.
 * Attendu ID : 710 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 5)
 *
 * Plan de cartes :
 *   « construction » (Ex.1) : la règle par l'exemple générique du cadrage —
 *     qcm_unique (« soustraire (-n) revient à additionner son opposé ») +
 *     texte_trou_drag (a - (-n) = a + {{+n}} = {{résultat}}).
 *   « mental » (Ex.6) : petits entiers, toutes formes d'écriture —
 *     texte_trou_saisie + qcm_unique.
 *   « transformer » (Ex.7) : le geste seul, SANS calcul (diagnostic) —
 *     texte_trou_drag ((+10) - (-12) = (+10) + {{(+12)}}).
 *   « decimaux » (Ex.4) : soustractions de décimaux (B1) — texte_trou_saisie.
 *
 * Politique : résultats signés imposés (+8, -5, 0), tiret clavier, dixièmes.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 710;
const L_CONSTRUCTION = 'Comment soustraire ? (la règle)';
const L_MENTAL = 'Calcul mental';
const L_TRANSFORMER = 'Transformer la soustraction en addition';
const L_DECIMAUX = 'Soustractions de décimaux';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frD = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};
const signeD = (d) => (d === 0 ? '0' : d < 0 ? frD(d) : `+${frD(d)}`);
const parenD = (d) => `(${signeD(d)})`;

const CONSIGNE_RES = 'Écris le résultat avec son signe (ex. : -5 ou +8), ou 0 s\'il est nul.';

// ═════════ Carte 1 — la règle (Ex.1) ═════════

const genConstructionQcm = () => {
    const n = (randomChoice([true, false]) ? 1 : -1) * randInt(2, 12) * 10;
    const correct = `Additionner son opposé, c'est-à-dire ${signeD(-n)}.`;
    const payload = makeQcm([correct,
        `Additionner ${signeD(n)}.`,
        `Soustraire ${signeD(-n)} : c'est pareil.`,
        'Ne rien changer : on enlève 0.'], correct,
    `<p><b>Que signifie « soustraire ${parenD(n)} » ?</b></p>`,
    opts(L_CONSTRUCTION));
    payload._meta.params = { n };
    return payload;
};

const genConstructionDrag = () => {
    const a = randInt(11, 99);                                     // décimal positif (dixièmes)
    const n = (randomChoice([true, false]) ? 1 : -1) * randInt(2, 9) * 10; // entier non nul
    const phrase = `${frD(a)} - ${parenD(n)} = ${frD(a)} + {{${signeD(-n)}}} = {{${signeD(a - n)}}}`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète : soustraire un nombre relatif revient à additionner son opposé.</b></p>',
        {
            ...opts(L_CONSTRUCTION), mode: 'drag',
            distracteurs: filtrerDistracteurs(phrase, [signeD(n), signeD(a + n), signeD(-(a - n))]).slice(0, 3),
        });
    payload._meta.params = { a, n };
    return payload;
};

// ═════════ Cartes 2 et 4 — calculs (Ex.6 / Ex.4) ═════════

/** (a, b, forme du second terme) — mental : petits entiers ; décimaux sinon. */
const tirerSoustraction = (decimal) => {
    const v = () => (decimal ? randInt(11, 239) : randInt(1, 13) * 10);
    const a = (randomChoice([true, false]) ? 1 : -1) * v();
    const b = (randomChoice([true, false]) ? 1 : -1) * v();
    return { a, b };
};

/** Affichage : 1er terme plain (ou parenthésé si décimaux), 2e parenthésé sauf positif nu. */
const exprSoustraction = ({ a, b }, decimal) => {
    const t1 = decimal ? parenD(a) : frD(a);
    const t2 = (b > 0 && !decimal && randomChoice([true, false])) ? frD(b) : parenD(b);
    return `${t1} - ${t2}`;
};

const genCalculSaisie = (decimal, label) => () => {
    const s = tirerSoustraction(decimal);
    const phrase = `${exprSoustraction(s, decimal)} = {{${signeD(s.a - s.b)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Effectue le calcul.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(label), mode: 'saisie' });
    payload._meta.params = s;
    return payload;
};

const genMentalQcm = () => {
    const s = tirerSoustraction(false);
    const r = s.a - s.b;
    const correct = signeD(r);
    const cands = [signeD(-r), signeD(s.a + s.b), signeD(r + 10), signeD(r - 10)];
    const distracteurs = [];
    cands.forEach(c => {
        if (distracteurs.length < 3 && c !== correct && !distracteurs.includes(c)) distracteurs.push(c);
    });
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p><b>Calcule mentalement : ${exprSoustraction(s, false)}</b></p>`,
        opts(L_MENTAL));
    payload._meta.params = s;
    return payload;
};

// ═════════ Carte 3 — transformer sans calculer (Ex.7) ═════════

const genTransformerDrag = () => {
    const decimal = randomChoice([true, false]);
    const v = () => (decimal ? randInt(11, 199) : randInt(2, 25) * 10);
    const a = (randomChoice([true, false]) ? 1 : -1) * v();
    const b = (randomChoice([true, false]) ? 1 : -1) * v();
    const phrase = `${parenD(a)} - ${parenD(b)} = ${parenD(a)} + {{${parenD(-b)}}}`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Transforme la soustraction en addition</b> (sans calculer le résultat).</p>',
        {
            ...opts(L_TRANSFORMER), mode: 'drag',
            distracteurs: filtrerDistracteurs(phrase, [parenD(b), parenD(-a), parenD(a)]).slice(0, 3),
        });
    payload._meta.params = { a, b };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    construction_qcm_unique: genConstructionQcm,
    construction_texte_trou_drag: genConstructionDrag,
    mental_texte_trou_saisie: genCalculSaisie(false, L_MENTAL),
    mental_qcm_unique: genMentalQcm,
    transformer_texte_trou_drag: genTransformerDrag,
    decimaux_texte_trou_saisie: genCalculSaisie(true, L_DECIMAUX),
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'construction', label: L_CONSTRUCTION, formatsList: buildFormats('construction') },
    { id: 'mental', label: L_MENTAL, formatsList: buildFormats('mental') },
    { id: 'transformer', label: L_TRANSFORMER, formatsList: buildFormats('transformer') },
    { id: 'decimaux', label: L_DECIMAUX, formatsList: buildFormats('decimaux') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
